// Cavelon's pedal helicopter: procedural wood-and-stone mesh + arcade flight
// model with a flywheel rotor, pedal energy, graded landings and
// tower/terrain/water collision.
import {
  MeshBuilder, StandardMaterial, Color3, Color4, TransformNode, Vector3,
  ParticleSystem, DynamicTexture,
} from '@babylonjs/core';
import { buildCaveman, animatePedal, animateExhausted, animateSit } from './caveman.js';
import {
  terrainHeight, WATER_Y, WALL_RADIUS, CAVES, HOME, PAD_R, LIANAS, ceilingY,
} from '../shared/level.mjs';

function mat(scene, name, hex, emissive = 0.12) {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.emissiveColor = m.diffuseColor.scale(emissive);
  m.specularColor = new Color3(0.05, 0.05, 0.05);
  return m;
}

// One soft dot shared by every damage-smoke system in the scene.
let smokeTex = null;
function smokeTexture(scene) {
  if (smokeTex && !smokeTex.isDisposed?.()) return smokeTex;
  const dt = new DynamicTexture('heliSmokePx', { width: 32, height: 32 }, scene, false);
  const c = dt.getContext();
  const grd = c.createRadialGradient(16, 16, 0, 16, 16, 16);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = grd; c.fillRect(0, 0, 32, 32);
  dt.update(); dt.hasAlpha = true;
  smokeTex = dt;
  return dt;
}

let loggedMeshCount = false;

// ---------------------------------------------------------------- mesh
export function buildHeliMesh(scene, { hue = 25 } = {}) {
  const root = new TransformNode('heli', scene);
  const tilt = new TransformNode('heliTilt', scene); tilt.parent = root; // visual pitch/roll
  const wood = mat(scene, 'h_wood', '#8a5a2b');
  const woodDark = mat(scene, 'h_woodD', '#6b4420');
  const leaf = mat(scene, 'h_leaf', '#4f8f3a');
  const vineM = mat(scene, 'h_vine', '#4c6b28');
  const skinM = mat(scene, 'h_skin', '#c9a06a');
  const playerM = new StandardMaterial('h_player', scene);
  playerM.diffuseColor = Color3.FromHSV(hue, 0.7, 0.8);
  playerM.emissiveColor = playerM.diffuseColor.scale(0.2);
  playerM.specularColor = new Color3(0.05, 0.05, 0.05);
  playerM.backFaceCulling = false;

  // skids: two logs
  for (const sx of [-1, 1]) {
    const skid = MeshBuilder.CreateCylinder('skid', { height: 3.4, diameter: 0.35, tessellation: 6 }, scene);
    skid.rotation.x = Math.PI / 2;
    skid.position.set(sx * 0.9, 0.18, 0);
    skid.material = woodDark; skid.parent = tilt;
    for (const sz of [-1, 1]) {
      const leg = MeshBuilder.CreateCylinder('leg', { height: 1.0, diameter: 0.22, tessellation: 5 }, scene);
      leg.position.set(sx * 0.9, 0.7, sz * 1.1);
      leg.rotation.z = -sx * 0.35;
      leg.material = woodDark; leg.parent = tilt;
    }
  }
  // seat log with a hide seat-pad and a small backrest
  const seat = MeshBuilder.CreateCylinder('seat', { height: 2.6, diameter: 0.6, tessellation: 7 }, scene);
  seat.rotation.x = Math.PI / 2; seat.position.y = 1.25;
  seat.material = wood; seat.parent = tilt;
  const pad = MeshBuilder.CreateBox('seatPad', { width: 0.8, height: 0.16, depth: 1.9 }, scene);
  pad.position.set(0, 1.58, -0.1); pad.material = skinM; pad.parent = tilt;
  const backrest = MeshBuilder.CreateBox('backrest', { width: 0.7, height: 0.55, depth: 0.12 }, scene);
  backrest.position.set(0, 1.85, -0.15); backrest.rotation.x = -0.2;
  backrest.material = skinM; backrest.parent = tilt;
  // mast with vine lashings
  const mast = MeshBuilder.CreateCylinder('mast', { height: 1.7, diameter: 0.28, tessellation: 6 }, scene);
  mast.position.set(0, 2.6, 0); mast.material = wood; mast.parent = tilt;
  for (const [ly, s] of [[1.9, 0.34], [2.35, 0.3], [3.28, 0.32]]) {
    const lash = MeshBuilder.CreateTorus('lash', { diameter: s, thickness: 0.07, tessellation: 8 }, scene);
    lash.position.set(0, ly, 0); lash.material = vineM; lash.parent = tilt;
  }
  // tail: boom + leaf fin + player banner + tail rotor, all hung off a swing
  // node so the whole tail can whip out of a hard yaw
  const tailN = new TransformNode('tailN', scene); tailN.parent = tilt;
  tailN.position.set(0, 1.7, -1.0);
  const boom = MeshBuilder.CreateCylinder('boom', { height: 2.6, diameterTop: 0.14, diameterBottom: 0.3, tessellation: 6 }, scene);
  boom.rotation.x = -Math.PI / 2 - 0.12; boom.position.set(0, 0, -1.2);
  boom.material = wood; boom.parent = tailN;
  const fin = MeshBuilder.CreateBox('fin', { width: 0.12, height: 0.9, depth: 0.7 }, scene);
  fin.position.set(0, 0.4, -2.4); fin.material = leaf; fin.parent = tailN;
  const banner = MeshBuilder.CreateDisc('banner', { radius: 0.55, tessellation: 3 }, scene);
  banner.position.set(0, 1.05, -2.35); banner.rotation.z = -Math.PI / 2;
  banner.material = playerM; banner.parent = tailN;

  // main rotor: 4 big palm fronds on a wooden hub + a blur disc at speed
  const rotor = new TransformNode('rotor', scene); rotor.parent = tilt; rotor.position.y = 3.5;
  const hub = MeshBuilder.CreateCylinder('hub', { height: 0.34, diameter: 0.55, tessellation: 7 }, scene);
  hub.material = woodDark; hub.parent = rotor;
  const bladePivots = [];
  for (let i = 0; i < 4; i++) {
    const bp = new TransformNode('bp' + i, scene); bp.parent = rotor; bp.rotation.y = (i * Math.PI) / 2;
    // frond: stretched flattened sphere + a thin mid-rib
    const frond = MeshBuilder.CreateSphere('frond' + i, { diameter: 1, segments: 5 }, scene);
    frond.scaling.set(0.95, 0.09, 4.6);
    frond.position.set(0, 0.08, 2.55); frond.rotation.x = 0.05;
    frond.material = leaf; frond.parent = bp;
    const rib = MeshBuilder.CreateBox('rib' + i, { width: 0.09, height: 0.1, depth: 4.5 }, scene);
    rib.position.set(0, 0.08, 2.5); rib.rotation.x = 0.05;
    rib.material = woodDark; rib.parent = bp;
    bladePivots.push(bp);
  }
  const blur = MeshBuilder.CreateDisc('rotorBlur', { radius: 4.7, tessellation: 28 }, scene);
  blur.rotation.x = Math.PI / 2; blur.position.y = 0.1;
  const blurM = new StandardMaterial('rotorBlurM', scene);
  blurM.diffuseColor = Color3.FromHexString('#4f8f3a');
  blurM.emissiveColor = Color3.FromHexString('#3d6f2e');
  blurM.alpha = 0; blurM.specularColor = Color3.Black();
  blurM.backFaceCulling = false;
  blur.material = blurM; blur.parent = rotor.parent; blur.position.y = 3.6;
  blur.isPickable = false;
  // tail rotor
  const tailRotor = new TransformNode('tailRotor', scene); tailRotor.parent = tailN;
  tailRotor.position.set(0.25, 0.4, -2.6);
  for (let i = 0; i < 3; i++) {
    const b = MeshBuilder.CreateBox('tb' + i, { width: 0.06, height: 1.1, depth: 0.22 }, scene);
    const bp = new TransformNode('tbp' + i, scene); bp.parent = tailRotor; bp.rotation.x = (i * Math.PI * 2) / 3;
    b.parent = bp; b.position.y = 0.55; b.material = wood;
  }
  // pedals + crank (the plates counter-rotate so they stay flat under the feet)
  const crank = new TransformNode('crank', scene); crank.parent = tilt; crank.position.set(0, 1.05, 0.9);
  const pedals = [];
  for (const s of [-1, 1]) {
    const p = MeshBuilder.CreateBox('pedal', { width: 0.35, height: 0.08, depth: 0.5 }, scene);
    const pp = new TransformNode('pp', scene); pp.parent = crank; pp.rotation.x = s > 0 ? 0 : Math.PI;
    p.parent = pp; p.position.set(s * 0.45, -0.45, 0); p.material = woodDark;
    pedals.push(p);
  }

  // Cavelon on the front seat, club slung on his back
  const pilot = buildCaveman(scene, { hue, scale: 1.05 });
  pilot.root.parent = tilt; pilot.root.position.set(0, 1.35, 0.55);
  pilot.baseY = 1.35;
  const clubShaft = MeshBuilder.CreateCylinder('club', { height: 0.9, diameter: 0.12, tessellation: 5 }, scene);
  clubShaft.position.set(0.18, 0.75, -0.28); clubShaft.rotation.z = 0.5;
  clubShaft.material = woodDark; clubShaft.parent = pilot.root;
  const clubHead = MeshBuilder.CreateSphere('clubHead', { diameter: 0.3, segments: 4 }, scene);
  clubHead.position.set(0.4, 1.1, -0.28); clubHead.material = woodDark; clubHead.parent = pilot.root;

  // passenger anchor on the back seat (empty until boarding)
  const paxSeat = new TransformNode('paxSeat', scene);
  paxSeat.parent = tilt; paxSeat.position.set(0, 1.35, -0.85);

  // damage smoke — built ONCE here, never per frame; idle (stopped) until the
  // hull is past 60 % wrecked
  const smokeAt = new TransformNode('smokeAt', scene);
  smokeAt.parent = tilt; smokeAt.position.set(0, 2.6, -0.6);
  const smoke = new ParticleSystem('heliSmoke', 60, scene);
  smoke.particleTexture = smokeTexture(scene);
  smoke.emitter = smokeAt;
  smoke.minSize = 0.6; smoke.maxSize = 2.0;
  smoke.minLifeTime = 0.5; smoke.maxLifeTime = 1.2;
  smoke.emitRate = 0;
  smoke.direction1 = new Vector3(-0.8, 1.2, -0.8);
  smoke.direction2 = new Vector3(0.8, 2.4, 0.8);
  smoke.gravity = new Vector3(0, 2, 0);
  smoke.color1 = new Color4(0.10, 0.10, 0.10, 0.75);
  smoke.color2 = new Color4(0.32, 0.30, 0.28, 0.55);
  smoke.colorDead = new Color4(0.2, 0.2, 0.2, 0);
  smoke.blendMode = ParticleSystem.BLENDMODE_STANDARD;

  // NOTE: collected AFTER the tail reparenting, so shadow casters and the
  // wreck-hiding sweep still see every primitive.
  const allMeshes = [...tilt.getChildMeshes()];
  if (!loggedMeshCount) {
    loggedMeshCount = true;
    console.log(`[heli] ${allMeshes.length} primitiver i allMeshes (skyggekastere)`);
  }
  return {
    root, tilt, rotor, tailRotor, tailN, crank, pedals, pilot, paxSeat,
    allMeshes, blurM, bladePivots, fin, smoke,
    crankAngle: 0, gearSquash: 0, pitchVis: 0, rollVis: 0, tailSway: 0, smokeOn: false,
  };
}

// ---------------------------------------------------------------- flight
export const GEAR_H = 1.15; // skid height over ground when parked

const LIFT = 15.5;          // rotor lift coefficient (see hoverRotor)

// Rotor setting that exactly holds a hover — HUD gauge and audio read this
// instead of hardcoding the constants.
export function hoverRotor(airspeed = 0, wet = 0, loaded = 0) {
  const tl = 1 + 0.22 * Math.min(1, airspeed / 16);
  const wetLift = (1 - 0.16 * wet) * (1 - 0.07 * loaded);
  return Math.pow(9.8 / (15.5 * wetLift * tl), 1 / 1.7);
}

export class Flight {
  constructor(colliders, events, obstacles = {}) {
    this.colliders = colliders;
    this.crowns = obstacles.crowns || [];   // soft foliage ellipsoids
    this.lianaHits = new Map();             // liana index -> last tangle time
    this.ev = events; // {onLand, onCrash, onThud, onFoliage, onTangle}
    this.windX = 0; this.windZ = 0;         // storm gusts, set by main
    this.loaded = 0;                        // 1 while carrying a passenger
    this._carryDmg = 0;                     // damage carried over a respawn
    this.reset();
  }

  reset() {
    this.pos = new Vector3(HOME.x, HOME.y + GEAR_H, HOME.z);
    this.vel = new Vector3(0, 0, 0);
    this.yaw = Math.PI;
    this.yawVel = 0;       // rad/s — the tail has mass now
    this.rotor = 0;        // 0..1 rotor speed, a flywheel that can reach 0 in the air
    this.energy = 100;
    this.damage = this._carryDmg || 0;   // a patched-up wreck stays a bit bent
    this._carryDmg = 0;
    this.landed = true;
    this.dead = false;
    this.wet = 0; // 0..1, set from the weather: slick pedals, heavy rotor
    this.sputter = false;  // pedals biting badly (low energy)
    this.reserve = false;  // pedalling on an empty tank — it costs the hull
    this.inCrown = false;  // inside crown foliage: shelter from lightning
    this.inFoliage = false;
    this._touch = null;    // touchdown speeds captured on the contact frame
    this.landImpulse = 0;
    this._ax = 0; this._az = 0;
    this.pitchVis = 0; this.rollVis = 0;
  }

  // Highest supported surface under (x,z) — terrain, cave pads, home pad, tower tops.
  groundAt(x, z, y) {
    let g = terrainHeight(x, z);
    const pads = [{ x: HOME.x, z: HOME.z, y: HOME.y, r: PAD_R + 4 },
      ...CAVES.map(c => ({ x: c.x, z: c.z, y: c.y, r: PAD_R + 2.5 }))];
    for (const p of pads) {
      // pad counts as ground only if we're above (or barely below) its surface
      if (Math.hypot(x - p.x, z - p.z) < p.r && y > p.y - 1.5 && p.y > g) g = p.y;
    }
    // The hall is SEALED: the rim wall grows past the analytic ceiling out
    // beyond r~505, and the ground clamp runs after the ceiling clamp — so
    // without this the rim is a ramp you can ride straight over the roof.
    return Math.min(g, ceilingY(x, z) - 2.4 - GEAR_H);
  }

  update(dt, input) {
    if (this.dead) return;
    this._t = (this._t || 0) + dt;
    const fwd = new Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw));

    // --- rotor / pedal: a flywheel. Pedalling spins it up, drag bleeds it
    // down, a fast descent windmills a little back in. It CAN reach 0 aloft.
    const pedaling = input.pedal;
    const sput = this.reserve ? 0.55 : (this.energy < 22 ? 0.55 + 0.45 * (this.energy / 22) : 1);
    const spin = pedaling ? 3.2 * sput : 0;
    this.sputter = sput < 1;
    const bleed = (this.landed && !pedaling) ? 1.2 : 0.30 + 0.25 * this.wet;
    const auto = Math.max(0, -this.vel.y) * 0.025;
    this.rotor += (spin * (1 - this.rotor) - bleed * this.rotor + auto) * dt;
    this.rotor = Math.max(0, Math.min(1, this.rotor));

    // --- energy: an empty tank does not stop Cavelon, it just costs him ribs
    if (this.energy <= 0 && input.pedal) { this.damage += 3.5 * dt; this.reserve = true; }
    else this.reserve = false;
    this.energy -= pedaling
      ? 1.9 * (1 + 0.4 * this.wet + 0.14 * this.loaded) * dt
      : (this.landed ? 0 : 0.35 * dt);
    this.energy = Math.max(0, Math.min(100, this.energy));

    // --- forces (rain-heavy rotor and a passenger on the back both cost lift;
    // moving forward buys you free translational lift)
    const g = 9.8;
    const wetLift = (1 - 0.16 * this.wet) * (1 - 0.07 * this.loaded);
    const airspeed = Math.hypot(this.vel.x, this.vel.z);
    const tl = 1 + 0.22 * Math.min(1, airspeed / 16);
    let ay = -g + LIFT * Math.pow(this.rotor, 1.7) * wetLift * tl;

    // analog throttle: keyboard gives ±1, the touch joystick anything between
    const throttle = input.throttle !== undefined
      ? input.throttle
      : (input.fwd ? 1 : 0) - (input.back ? 1 : 0);
    let ax = 0, az = 0;
    if (!this.landed || this.rotor > 0.3) {
      const thrust = throttle >= 0 ? 15 * throttle : 9 * throttle;
      ax += fwd.x * thrust * Math.max(0.35, this.rotor);
      az += fwd.z * thrust * Math.max(0.35, this.rotor);
    }
    // a dive trades height for speed; pulling back flares it into lift
    if (this.vel.y < 0) {
      const conv = Math.min(5, (-this.vel.y) * 0.45);
      ax += fwd.x * conv; az += fwd.z * conv;
    }
    if (throttle < 0) ay += (-throttle) * airspeed * 0.12;
    this._throttle = throttle;

    // --- yaw with mass: the tail swings out and settles again
    const yawTarget = (input.turn || 0) * 1.9 * (this.landed ? 0.5 : 1);
    this.yawVel += (yawTarget - this.yawVel) * Math.min(1, dt * 7);
    this.yaw += this.yawVel * dt;

    // drag
    ax -= this.vel.x * 0.55; az -= this.vel.z * 0.55;
    ay -= this.vel.y * (this.vel.y < 0 ? 0.28 : 0.5);

    // world edge: headwind pushes you back into the valley
    const r = Math.hypot(this.pos.x, this.pos.z);
    if (r > WALL_RADIUS) {
      ax -= (this.pos.x / r) * 22; az -= (this.pos.z / r) * 22;
    }

    // storm gusts
    ax += this.windX; az += this.windZ;

    // --- the closed leaf roof: a hard ceiling you scrape your rotor on
    const ceil = ceilingY(this.pos.x, this.pos.z) - 2.4;
    if (this.pos.y > ceil) {
      const impact = this.vel.y;
      this.pos.y = ceil;
      if (this.vel.y > 0) this.vel.y = 0;
      if (impact > 5) {
        this.damage += (impact - 5) * 2.5;
        this.ev.onThud(Math.min(0.45, impact / 25));
      }
      this.inFoliage = true; // rustle/leaf feedback via the foliage channel
      if (this.ev.onFoliage) this.ev.onFoliage();
    } else this.inFoliage = false;

    // --- crown foliage: SHELTER, not a blender. It drags, it pushes you back
    // down under the leaves — and it hides you from the lightning. No damage.
    this.inCrown = false;
    for (const c of this.crowns) {
      const dx = this.pos.x - c.x, dz = this.pos.z - c.z;
      if (Math.abs(dx) > c.r || Math.abs(dz) > c.r) continue;
      const dy = this.pos.y - c.y;
      if ((dx * dx + dz * dz) / (c.r * c.r) + (dy * dy) / (c.ry * c.ry) < 1) {
        this.inFoliage = true;
        this.inCrown = true;
        const damp = Math.exp(-2.0 * dt);
        this.vel.x *= damp; this.vel.z *= damp;
        this.vel.y = Math.min(this.vel.y, 2) - 7 * dt;
        if (this.ev.onFoliage) this.ev.onFoliage();
        break;
      }
    }

    // --- lianas: a rope across the rotor whips you round the side you passed
    // it on — deterministic, so you can learn to thread them
    for (let i = 0; i < LIANAS.length; i++) {
      const l = LIANAS[i];
      const dx = this.pos.x - l.x, dz = this.pos.z - l.z;
      if (dx * dx + dz * dz > 3.24) continue;   // circular r = 1.8 m
      if (this.pos.y < l.topY - l.len - 1 || this.pos.y > l.topY + 1) continue;
      const last = this.lianaHits.get(i) || -10;
      if (this._t - last < 1.6) continue;
      this.lianaHits.set(i, this._t);
      const hsp = Math.hypot(this.vel.x, this.vel.z);
      const side = Math.sign(this.vel.x * dz - this.vel.z * dx) || 1;
      this.yawVel += side * 2.2;
      this.rotor *= 0.78;
      this.vel.x *= 0.6; this.vel.z *= 0.6;
      this.damage += 1.2 + 0.22 * hsp;
      if (this.ev.onTangle) this.ev.onTangle();
    }

    this._ax = ax; this._az = az;   // kept for the physics-driven visual tilt

    this.vel.x += ax * dt; this.vel.y += ay * dt; this.vel.z += az * dt;
    const hspeed = Math.hypot(this.vel.x, this.vel.z);
    if (hspeed > 34) { this.vel.x *= 34 / hspeed; this.vel.z *= 34 / hspeed; }
    if (this.vel.y > 9) this.vel.y = 9;

    this.pos.x += this.vel.x * dt;
    this.pos.y += this.vel.y * dt;
    this.pos.z += this.vel.z * dt;

    // --- tower side collision (cylinders below their top)
    for (const c of this.colliders) {
      const dx = this.pos.x - c.x, dz = this.pos.z - c.z;
      const d = Math.hypot(dx, dz);
      if (d < c.r && this.pos.y < c.topY - 0.5) {
        const nx = dx / (d || 1), nz = dz / (d || 1);
        this.pos.x = c.x + nx * c.r; this.pos.z = c.z + nz * c.r;
        const vn = this.vel.x * nx + this.vel.z * nz;
        if (vn < 0) {
          this.vel.x -= vn * nx * 1.6; this.vel.z -= vn * nz * 1.6; // bounce
          if (vn < -6) { this.damage += (-vn - 6) * 3; this.ev.onThud(-vn / 20); }
        }
      }
    }

    // --- ground / landing. The touchdown speeds are captured on the CONTACT
    // frame; the skids may slide for a while before we call it a landing, and
    // by then the vertical speed is long gone.
    const ground = this.groundAt(this.pos.x, this.pos.z, this.pos.y);
    const floorY = ground + GEAR_H;
    if (this.pos.y <= floorY) {
      const impact = -this.vel.y, slide = Math.hypot(this.vel.x, this.vel.z);
      if (!this.landed && !this._touch) this._touch = { vy: Math.max(0, impact), vh: slide };
      this.pos.y = floorY; this.vel.y = 0;
      // water: terrain below the surface means we hit the river, not land
      if (ground < WATER_Y - 0.3) { this.crash('splash'); return; }
      if (impact > 4.5) {
        const dmg = (impact - 4.5) * 7;
        this.damage += dmg;
        this.landImpulse = impact;
        this.ev.onThud(Math.min(0.6, impact / 25));
      }
      // strong ground friction
      this.vel.x *= Math.max(0, 1 - 6 * dt);
      this.vel.z *= Math.max(0, 1 - 6 * dt);
      if (!this.landed && Math.hypot(this.vel.x, this.vel.z) < 2.5) {
        this.landed = true;
        this.landImpulse = this._touch.vy;
        this.ev.onLand(this._touch.vy, this._touch.vh, ground);
        this._touch = null;
      }
    } else if (this.pos.y > floorY + 0.6 && this.landed) {
      this.landed = false;
      this._touch = null;
    }

    if (this.damage >= 100) { this.crash('wreck'); return; }

    // --- visual tilt, driven by the forces that are actually acting
    const fwdAcc = this._ax * fwd.x + this._az * fwd.z;
    const sideVel = this.vel.x * fwd.z - this.vel.z * fwd.x;
    const tgtPitch = Math.max(-0.20, Math.min(0.30, fwdAcc * 0.022));
    const tgtRoll = Math.max(-0.45, Math.min(0.45, -this.yawVel * 0.16 - sideVel * 0.012));
    this.pitchVis += (tgtPitch - this.pitchVis) * Math.min(1, dt * 5);
    this.rollVis += (tgtRoll - this.rollVis) * Math.min(1, dt * 7);
  }

  crash(reason) {
    this.dead = true;
    this.ev.onCrash(reason, this.pos.clone());
  }

  // which cave pad (or 'home') we are parked on, if any
  padUnder() {
    if (!this.landed) return null;
    if (Math.hypot(this.pos.x - HOME.x, this.pos.z - HOME.z) < PAD_R + 4 &&
        Math.abs(this.pos.y - GEAR_H - HOME.y) < 2) return 'home';
    for (const c of CAVES) {
      if (Math.hypot(this.pos.x - c.x, this.pos.z - c.z) < PAD_R + 2.5 &&
          Math.abs(this.pos.y - GEAR_H - c.y) < 2) return c.id;
    }
    return null;
  }
}

// ---------------------------------------------------------------- visuals
// Apply flight state to the visual mesh; animate rotor/pedals/pilot, the
// suspension, the tail swing and the damage silhouette.
export function applyHeliVisual(h, flight, t, dt) {
  h.root.position.copyFrom(flight.pos);
  h.root.rotation.y = flight.yaw;
  const d = Math.max(0, Math.min(1, flight.damage / 100));

  // one absolute crank angle drives crank, pedal plates and Cavelon's legs
  h.crankAngle = (h.crankAngle || 0) + flight.rotor * 12 * dt;
  h.crank.rotation.x = h.crankAngle;
  for (const p of h.pedals) p.rotation.x = -h.crankAngle;

  // skid suspension: a hard touchdown squashes the frame, then it springs back
  if ((flight.landImpulse || 0) > 0) {
    h.gearSquash = Math.min(1, flight.landImpulse / 12);
    flight.landImpulse = 0;
  } else {
    h.gearSquash = (h.gearSquash || 0) * Math.exp(-9 * dt);
  }
  const gs = h.gearSquash;
  h.tilt.position.y = -0.25 * gs;
  h.tilt.scaling.y = 1 - 0.10 * gs;

  // foliage shakes the whole frame; a beaten-up frame shakes on its own
  const shake = flight.inFoliage ? 0.06 : 0;
  h.tilt.rotation.x = flight.pitchVis + Math.sin(t * 31) * shake + Math.sin(t * 23) * 0.015 * d;
  h.tilt.rotation.z = flight.rollVis + Math.cos(t * 27) * shake;

  h.rotor.rotation.y += flight.rotor * 22 * dt;
  h.rotor.rotation.z = Math.sin(h.crankAngle * 3) * 0.03 * d;
  h.tailRotor.rotation.x += flight.rotor * 30 * dt;

  // blade coning rises with rotor speed
  const cone = -(0.05 + 0.13 * flight.rotor);
  for (const bp of h.bladePivots) bp.rotation.z = cone;
  // damage silhouette: a drooping, shortened blade, then smoke, then a bent fin
  if (h.bladePivots[1]) {
    if (d > 0.35) {
      h.bladePivots[1].rotation.z = -0.30 * (d - 0.35) / 0.65;
      h.bladePivots[1].scaling.z = 1 - 0.25 * d;
    } else h.bladePivots[1].scaling.z = 1;
  }
  if (h.smoke) {
    const rate = Math.max(0, (d - 0.6) * 90);
    h.smoke.emitRate = rate;
    if (rate > 0 && !h.smokeOn) { h.smoke.start(); h.smokeOn = true; }
    else if (rate <= 0 && h.smokeOn) { h.smoke.stop(); h.smokeOn = false; }
  }
  if (h.fin) h.fin.rotation.z = d > 0.75 ? 0.55 : 0;

  // the tail whips out of a hard yaw and trails back into line
  const swayTgt = Math.max(-0.25, Math.min(0.25, -(flight.yawVel || 0) * 0.10));
  h.tailSway = (h.tailSway || 0) + (swayTgt - (h.tailSway || 0)) * Math.min(1, dt * 6);
  if (h.tailN) h.tailN.rotation.y = h.tailSway;

  if (h.blurM) h.blurM.alpha = Math.min(0.30, Math.max(0, flight.rotor - 0.45) * 0.55);
  // an exhausted Cavelon pedals with shorter, heavier strokes — but he never
  // stops while the rotor turns, not even on reserve
  if (flight.rotor > 0.05) {
    const e = 1 - Math.min(1, flight.energy / 35);
    animatePedal(h.pilot, h.crankAngle, 0.55 - 0.25 * e);
    // must run EVERY frame (also with e = 0) or the slump stays hanging
    animateExhausted(h.pilot, e, t, flight.windX || 0, dt);
  } else animateSit(h.pilot);
}

const wrapPi = a => Math.atan2(Math.sin(a), Math.cos(a));

// Remote players: derive bank/pitch and pedal cadence from two interpolated
// samples — no extra network traffic, just the data we already relay.
export function applyRemoteVisual(mesh, s, prev, t, dt) {
  if (!mesh || !s || !prev || dt <= 0) return;
  const vx = (s.p[0] - prev.p[0]) / dt;
  const vz = (s.p[2] - prev.p[2]) / dt;
  const dyaw = wrapPi(s.yaw - prev.yaw) / dt;
  const fwdX = Math.sin(s.yaw), fwdZ = Math.cos(s.yaw);
  const fwdSpeed = vx * fwdX + vz * fwdZ;
  const carrying = s.carrying || 0;
  // 120 ms interpolated samples are noisy under packet loss: clamp hard, lerp slow
  let tgtPitch = Math.max(-0.18, Math.min(0.26, fwdSpeed / 18));
  tgtPitch -= 0.05 * carrying;
  const tgtRoll = -Math.max(-1, Math.min(1, dyaw / 1.7)) * 0.3;
  const k = Math.min(1, dt * 5);
  mesh.pitchVis = (mesh.pitchVis || 0) + (tgtPitch - (mesh.pitchVis || 0)) * k;
  mesh.rollVis = (mesh.rollVis || 0) + (tgtRoll - (mesh.rollVis || 0)) * k;
  mesh.root.position.set(s.p[0], s.p[1], s.p[2]);
  mesh.root.rotation.y = s.yaw;
  mesh.tilt.rotation.x = mesh.pitchVis;
  mesh.tilt.rotation.z = mesh.rollVis;
  mesh.tilt.position.y = -0.06 * carrying;   // a loaded taxi hangs in the tail

  const rot = s.rot || 0;
  mesh.rotor.rotation.y += rot * 22 * dt;
  mesh.tailRotor.rotation.x += rot * 30 * dt;
  mesh.crankAngle = (mesh.crankAngle || 0) + rot * 12 * dt;
  mesh.crank.rotation.x = mesh.crankAngle;
  for (const p of mesh.pedals) p.rotation.x = -mesh.crankAngle;
  if (mesh.blurM) mesh.blurM.alpha = Math.min(0.30, Math.max(0, rot - 0.45) * 0.55);
  const cone = -(0.05 + 0.13 * rot);
  for (const bp of mesh.bladePivots) bp.rotation.z = cone;
  if (rot > 0.05) animatePedal(mesh.pilot, mesh.crankAngle);
  else animateSit(mesh.pilot);
}
